/** Cookie consent preferences for deskreplay.com (marketing site only). */

import { isGlobalPrivacyControlEnabled } from "@/lib/privacySignals";

export const COOKIE_CONSENT_KEY = "deskreplay_cookie_consent_v2";
export const COOKIE_CONSENT_EVENT = "deskreplay-cookie-consent";
export const COOKIE_PREFERENCES_OPEN_EVENT = "deskreplay-open-cookie-preferences";

/** Bump when the Cookie Policy changes in a way that requires a fresh choice. */
export const CONSENT_POLICY_REVISION = 5;

/** Re-show the banner after this period (typical practice: 6–12 months). We use 12 months. */
export const CONSENT_TTL_MS = 365 * 24 * 60 * 60 * 1000;

export type CookieConsentTier = "reject" | "essential" | "accept_all";

export type CookieConsentChoice = {
  version: 2;
  /** Always true when stored. Includes consent memory, hosting, and pilot signup CAPTCHA. */
  necessary: true;
  tier: CookieConsentTier;
  policyRevision: number;
  updatedAt: string;
};

export const COOKIE_CONSENT_VERSION = 2 as const;

function baseChoice(tier: CookieConsentTier): CookieConsentChoice {
  return {
    version: COOKIE_CONSENT_VERSION,
    necessary: true,
    tier,
    policyRevision: CONSENT_POLICY_REVISION,
    updatedAt: new Date().toISOString(),
  };
}

export function rejectConsent(): CookieConsentChoice {
  return baseChoice("reject");
}

/** Essential site use with measurement (GA4, Clarity) enabled. */
export function essentialConsent(): CookieConsentChoice {
  return baseChoice("essential");
}

export function acceptAllConsent(): CookieConsentChoice {
  return baseChoice("accept_all");
}

/** @deprecated Use essentialConsent(). Kept for legacy call sites. */
export function necessaryOnlyConsent(): CookieConsentChoice {
  return essentialConsent();
}

function parseTier(raw: unknown): CookieConsentTier | null {
  if (raw === "reject" || raw === "essential" || raw === "accept_all") return raw;
  return null;
}

function isConsentFresh(choice: CookieConsentChoice): boolean {
  if (choice.policyRevision !== CONSENT_POLICY_REVISION) return false;
  const at = Date.parse(choice.updatedAt);
  if (!Number.isFinite(at)) return false;
  return Date.now() - at < CONSENT_TTL_MS;
}

function normalizeLegacyConsent(raw: unknown): CookieConsentChoice | null {
  if (!raw || typeof raw !== "object") return null;
  const o = raw as Record<string, unknown>;
  if (o.necessary !== true) return null;
  if (o.version !== 1 && o.version !== 2) return null;

  let tier = parseTier(o.tier);
  if (!tier) {
    if (o.analytics === false) tier = "reject";
    else if (o.analytics === true) tier = "accept_all";
    else return null;
  }

  const choice: CookieConsentChoice = {
    version: 2,
    necessary: true,
    tier,
    policyRevision:
      typeof o.policyRevision === "number" ? o.policyRevision : CONSENT_POLICY_REVISION,
    updatedAt: typeof o.updatedAt === "string" ? o.updatedAt : new Date().toISOString(),
  };
  return isConsentFresh(choice) ? choice : null;
}

export function readCookieConsent(): CookieConsentChoice | null {
  if (typeof window === "undefined") return null;
  try {
    for (const key of [COOKIE_CONSENT_KEY, "deskreplay_cookie_consent_v1"]) {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = normalizeLegacyConsent(JSON.parse(raw));
      if (parsed) {
        if (key !== COOKIE_CONSENT_KEY) writeCookieConsent(parsed);
        return parsed;
      }
    }
    return null;
  } catch {
    return null;
  }
}

export function writeCookieConsent(choice: CookieConsentChoice): void {
  if (typeof window === "undefined") return;
  localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(choice));
  try {
    localStorage.removeItem("deskreplay_cookie_consent_v1");
  } catch {
    /* ignore */
  }
  window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_EVENT, { detail: choice }));
}

export function hasAnalyticsConsent(): boolean {
  const tier = readCookieConsent()?.tier;
  return tier === "essential" || tier === "accept_all";
}

/** Apply GPC as measurement opt-out when the user has not saved a choice yet. */
export function bootstrapPrivacySignals(): void {
  if (typeof window === "undefined") return;
  if (readCookieConsent()) return;
  if (isGlobalPrivacyControlEnabled()) {
    writeCookieConsent(rejectConsent());
  }
}
